import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useUrlState } from '../nav/urlState';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln } from '../catalog/money';
import { HistoryPanel } from '../history/HistoryPanel';
import { useDebounced } from '../kits/KitEditor';
import { RowMenu } from '../ui/RowMenu';
import { ConfirmDelete, PeopleNav, useRememberPeopleTab } from './FunctionsPage';
import { DuplicateNotice } from './DuplicateNotice';
import { PersonForm } from './PersonForm';
import { leaveGuarded } from '../ui/unsaved';
import { PersonContracts } from '../contracts/ContractsPage';
import { EMPLOYMENT_LABELS, FUNCTIONS_RESOURCE, PEOPLE_KEY, PERSON_RATES_RESOURCE, PERSONS_RESOURCE, type FunctionSummary, type Person } from './types';

type ListResponse = { persons: Person[]; truncated: boolean };

/** What the person costs, as far as the role sees it: the own rate (model "stały") or "wg funkcji". */
function costText(p: Person): string {
  if (p.costModel === 'staly') return p.rateGrosze != null ? formatPln(p.rateGrosze) : '—';
  return 'wg funkcji';
}

const prefillFrom = (u: { id: string; name: string; email: string } | undefined) => (u ? { name: u.name, email: u.email, userId: u.id } : undefined);

export function PeoplePage() {
  const { me } = useMe();
  useRememberPeopleTab();
  // P13: the search, the function and the open form live in the address ("Wstecz" comes back to them)
  const { params, update } = useUrlState();
  const search = params.get('szukaj') ?? '';
  const setSearch = (v: string) => update({ szukaj: v });
  const functionId = params.get('funkcja') ?? '';
  const setFunctionId = (v: string) => update({ funkcja: v });
  // from "Użytkownicy": `?osoba=<id>` opens that person, `?konto=<userId>` a new person made from the account
  const fromAccount = params.get('konto');
  // `undefined`: form closed; `null`: adding (`?nowa=1`); an id: editing that person
  const editing: string | null | undefined = params.get('osoba') ?? (fromAccount || params.get('nowa') === '1' ? null : undefined);
  const setEditing = (id: string | null) => update(id === null ? { nowa: '1' } : { osoba: id });
  const close = () => update({ osoba: null, konto: null, nowa: null });
  const [deleting, setDeleting] = useState<Person | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const q = useDebounced(search.trim(), 250);
  const seeFunctions = me !== undefined && can(me, FUNCTIONS_RESOURCE, 'view');
  const allowed = me !== undefined && can(me, PERSONS_RESOURCE, 'view');

  const list = useQuery({
    queryKey: [...PEOPLE_KEY, 'persons', q, functionId],
    // keep the rows (and an open form) on screen while a new search loads
    placeholderData: keepPreviousData,
    enabled: allowed,
    queryFn: () => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (functionId) params.set('functionId', functionId);
      const qs = params.toString();
      return api<ListResponse>(`/api/people/persons${qs ? `?${qs}` : ''}`);
    },
  });
  const account = useQuery({
    queryKey: ['admin-users'],
    enabled: fromAccount !== null && editing === null,
    queryFn: () => api<{ users: { id: string; name: string; email: string }[] }>('/api/admin/users'),
  });
  const fnList = useQuery({ queryKey: [...PEOPLE_KEY, 'functions'], enabled: seeFunctions, queryFn: () => api<{ functions: FunctionSummary[] }>('/api/people/functions') });
  if (!me) return null;
  // a role that sees only the functions goes straight to them
  if (!allowed) return seeFunctions ? <Navigate to="/osoby/funkcje" replace /> : <Navigate to="/" replace />;

  const persons = list.data?.persons ?? [];
  const functions = seeFunctions ? (fnList.data?.functions ?? []) : null;
  const fnName = new Map((functions ?? []).map((f) => [f.id, f.name ?? '']));
  const visible = (field: string) => fieldVisible(me, PERSONS_RESOURCE, field);
  const functionRateVisible = fieldVisible(me, PERSON_RATES_RESOURCE, 'rateGrosze');
  const showRate = visible('rateGrosze');
  const showFunctions = visible('functions') && functions !== null;
  const seeHistory = can(me, 'history.entries', 'view');
  const columns = 3 + (showFunctions ? 1 : 0) + (showRate ? 1 : 0);
  const editingPerson = editing ? persons.find((p) => p.id === editing) : undefined;

  return (
    <section className="page" data-feature-id="people.list">
      <div className="page-head">
        <h1>Osoby i funkcje</h1>
        {can(me, PERSONS_RESOURCE, 'create') && editing === undefined && (
          <button className="btn primary" type="button" data-feature-id="people.edit" onClick={() => leaveGuarded(() => setEditing(null))}>
            Dodaj osobę
          </button>
        )}
      </div>
      <PeopleNav me={me} />
      <p className="muted">Ludzie, z którymi pracujemy: na etacie, stali współpracownicy i osoby z zewnątrz, z funkcjami, które mogą pełnić.</p>

      {editing === null && (fromAccount === null || !account.isLoading) && (
        <PersonForm functions={functions} visible={visible} functionRateVisible={functionRateVisible} prefill={prefillFrom(account.data?.users.find((u) => u.id === fromAccount))} onClose={close} />
      )}
      {editingPerson && <PersonForm key={editingPerson.id} person={editingPerson} functions={functions} visible={visible} functionRateVisible={functionRateVisible} onClose={close} />}
      {editingPerson && <PersonContracts key={`umowy-${editingPerson.id}`} personId={editingPerson.id} me={me} />}

      <div className="filters">
        <label className="field">
          <span>Szukaj</span>
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Imię, nazwisko, telefon, e-mail" autoComplete="off" />
        </label>
        {functions !== null && (
          <label className="field">
            <span>Funkcja</span>
            <select value={functionId} onChange={(e) => setFunctionId(e.target.value)}>
              <option value="">Wszystkie</option>
              {functions.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać osób'}</p>}
      {list.data && persons.length === 0 && <p className="muted">Nie znaleziono osób.</p>}
      {list.data?.truncated && <p className="notice">Pokazano pierwszych 200 osób. Zawęź wyszukiwanie, żeby zobaczyć pozostałe.</p>}

      {persons.length > 0 && (
        <div className="table-wrap">
          <table className="table tpl-table people-table" aria-label="Osoby">
            <thead>
              <tr>
                <th scope="col">Imię i nazwisko</th>
                {showFunctions && (
                  <th scope="col" className="wide-only">
                    Funkcje
                  </th>
                )}
                <th scope="col" className="wide-only">
                  Zatrudnienie
                </th>
                {showRate && (
                  <th scope="col" className="num wide-only">
                    Stawka
                  </th>
                )}
                <th scope="col" className="tpl-actions-col">
                  <span className="visually-hidden">Działania</span>
                </th>
              </tr>
            </thead>
            {persons.map((p) => {
              const more = [
                ...(seeHistory ? [{ label: historyId === p.id ? 'Ukryj historię zmian' : 'Historia zmian', onSelect: () => setHistoryId(historyId === p.id ? null : p.id) }] : []),
                ...(can(me, PERSONS_RESOURCE, 'delete') ? [{ label: 'Usuń', danger: true, onSelect: () => setDeleting(p) }] : []),
              ];
              const contact = [p.phone, p.email].filter(Boolean).join(' · ');
              return (
                <tbody key={p.id} aria-label={p.name}>
                  <tr>
                    <td>
                      <strong>{p.name}</strong>
                      {p.userId && <span className={`chip people-account${p.accountActive === false ? ' off' : ''}`}>{p.accountActive === false ? 'konto nieaktywne' : 'konto'}</span>}
                      {contact && <div className="muted">{contact}</div>}
                      <DuplicateNotice person={p} />
                      {showFunctions && (p.functions ?? []).length > 0 && (
                        <div className="chips phone-only" aria-hidden="true">
                          {(p.functions ?? []).map((f) => (
                            <span key={f.functionId} className="chip">
                              {fnName.get(f.functionId)}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    {showFunctions && (
                      <td className="wide-only">
                        <div className="chips people-fns">
                          {(p.functions ?? []).map((f) => (
                            <span key={f.functionId} className="chip">
                              {fnName.get(f.functionId)}
                            </span>
                          ))}
                        </div>
                      </td>
                    )}
                    <td className="wide-only">{p.employment ? EMPLOYMENT_LABELS[p.employment] : '—'}</td>
                    {showRate && <td className="num wide-only nowrap">{costText(p)}</td>}
                    <td className="tpl-actions">
                      <button className="btn small" type="button" data-feature-id="people.edit" aria-label={`Edytuj: ${p.name ?? ''}`} onClick={() => leaveGuarded(() => setEditing(p.id))}>
                        {can(me, PERSONS_RESOURCE, 'edit') ? 'Edytuj' : 'Szczegóły'}
                      </button>
                      <RowMenu label={`Więcej działań: ${p.name ?? ''}`} actions={more} />
                    </td>
                  </tr>
                  {historyId === p.id && seeHistory && (
                    <tr>
                      <td colSpan={columns}>
                        <HistoryPanel entityId={p.id} resource={PERSONS_RESOURCE} />
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })}
          </table>
        </div>
      )}
      {deleting && (
        <ConfirmDelete
          title="Usunąć osobę?"
          text={`„${deleting.name ?? ''}” zniknie z listy i z wyboru do obsady. W historii zmian zostaje wpis o usunięciu.`}
          confirm="Usuń osobę"
          url={`/api/people/persons/${deleting.id}?version=${deleting.version}`}
          onClose={() => setDeleting(null)}
        />
      )}
    </section>
  );
}
